import BackButton from 'common/BackButton/BackButton';
import SearchBar from './SearchBar/SearchBar';
import Loader from 'components/Loader/Loader';

import DancerCard from './Cards/DancerCard/DancerCard';
import CoachCard from './Cards/CoachCard/CoachCard';
import JudgeCard from './Cards/JudgeCard/JudgeCard';
import ClubCard from './Cards/ClubCard/ClubCard';

import { Link, useLocation, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { useRegistryFilters } from 'hooks/useRegistryFilters';
import { useRegistryList } from 'hooks/useRegistryList';
import ErrorState from 'common/ErrorState/ErrorState';

import './Catalog.css';
import { useLanguage } from 'hooks/useLanguage';

const TITLES = {
  dancers: { ua: 'Танцюристи', en: 'Dancers' },
  clubs: { ua: 'Клуби', en: 'Clubs' },
  coaches: { ua: 'Тренери', en: 'Coaches' },
  judges: { ua: 'Судді', en: 'Judges' },
};

const ARCHIVE_TITLES = {
  dancers: { ua: 'Архів танцюристів', en: 'Dancers archive' },
  clubs: { ua: 'Архів клубів', en: 'Clubs archive' },
  coaches: { ua: 'Архів тренерів', en: 'Coaches archive' },
  judges: { ua: 'Архів суддів', en: 'Judges archive' },
};

/**
 * Реєстр учасників — ADR-0005, ADR-0015.
 *
 * Реєстр і архів — один і той самий екран, бо різниця між ними лишилася в
 * джерелі: архів питається `registryState=ARCHIVE`. Сторінка більше не
 * вирішує, кого вважати чинним, і не тримає ані списку статусів, ані
 * клієнтського відбору за станом сплати.
 */
export default function Catalog() {
  const language = useLanguage();
  const { catalogs } = useParams();
  const { pathname } = useLocation();

  const isArchive = pathname.endsWith('/archive');

  const { danceClasses, judgeCategories } = useRegistryFilters();
  const registry = useRegistryList(catalogs, { archive: isArchive });

  const [sentinelNode, setSentinelNode] = useState(null);
  const { loadMore } = registry;

  // Нескінченне гортання: сторінка довантажується, коли до кінця переліку
  // лишається екран.
  useEffect(() => {
    if (!sentinelNode || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && loadMore(), {
      rootMargin: '300px',
    });

    observer.observe(sentinelNode);
    return () => observer.disconnect();
  }, [sentinelNode, loadMore]);

  const title = (isArchive ? ARCHIVE_TITLES : TITLES)[catalogs]?.[language] || '';

  function renderCard(entity) {
    const card =
      catalogs === 'dancers' ? (
        <DancerCard dancer={entity} />
      ) : catalogs === 'clubs' ? (
        <ClubCard club={entity} />
      ) : catalogs === 'coaches' ? (
        <CoachCard coach={entity} />
      ) : catalogs === 'judges' ? (
        <JudgeCard judge={entity} />
      ) : null;

    if (!card) return null;

    // Архів позначаємо стрічкою й притлумленим виглядом самої картки, а не
    // текстом статусу — той у продукті ніде не показуємо.
    if (!isArchive) return <div key={entity.id}>{card}</div>;

    return (
      <div className='archive-card-frame' key={entity.id}>
        {card}
        <span className='archive-card-badge'>{language === 'en' ? 'Archive' : 'Архів'}</span>
      </div>
    );
  }

  return (
    <div className='catalog section'>
      <div className='container'>
        <BackButton />

        <header className='catalog-head'>
          <h1 className='page-title'>{title}</h1>

          <div className='catalog-head-meta'>
            {!registry.isLoading && registry.total > 0 && (
              <p className='catalog-count tnum'>
                {registry.total} {language === 'en' ? 'records' : 'записів'}
              </p>
            )}

            {!isArchive && (
              <Link className='btn btn--secondary catalog-archive-link' to={`/catalogs/${catalogs}/archive`}>
                {language === 'en' ? 'Archive' : 'Архів'}
                <svg width='16' height='16' viewBox='0 0 24 24' aria-hidden='true'>
                  <path
                    d='M5 12h14M13 6l6 6-6 6'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </Link>
            )}
          </div>
        </header>

        <SearchBar
          catalogs={catalogs}
          danceClasses={danceClasses}
          judgeCategories={judgeCategories}
          loading={registry.isLoading}
          total={registry.total}
          loadedCount={registry.items.length}
          onSearch={registry.search}
          onReset={registry.reset}
          isSearching={registry.isFiltered}
        />

        {registry.status === 'failed' && <ErrorState messages={registry.errors} onRetry={registry.retry} />}

        {registry.isLoading ? (
          <Loader variant='cards' count={12} />
        ) : registry.items.length > 0 ? (
          <>
            <div className={`catalog-wrapper catalog-wrapper--${catalogs}`}>{registry.items.map(renderCard)}</div>

            {/* Скелет довантаження стоїть поруч зі спостережуваною міткою, а не
                всередині неї: мітка заввишки в піксель, і вкладені в неї картки
                виходили за межі потоку та накривали підвал. */}
            {registry.hasMore && <div ref={setSentinelNode} className='catalog-sentinel' />}
            {registry.isLoadingMore && <Loader variant='cards' count={4} />}
          </>
        ) : (
          registry.status === 'succeeded' && (
            <p className='no-dancers-searched'>
              {registry.isFiltered
                ? language === 'en'
                  ? 'Nothing was found for your request. Try changing the filters.'
                  : 'За вашим запитом нічого не знайдено. Спробуйте змінити фільтри.'
                : isArchive
                ? language === 'en'
                  ? 'The archive is empty.'
                  : 'Архів порожній.'
                : language === 'en'
                ? 'The register is empty.'
                : 'Реєстр порожній.'}
            </p>
          )
        )}
      </div>
    </div>
  );
}
